export interface CanvasPerformanceSettings {
  alwaysHideEdges: boolean
  hideEdgesDuringInteraction: boolean
  imagePreviewOnlyDuringInteraction: boolean
  lightweightImageNodeShell: boolean
  lightweightNodeChrome: boolean
  previewPreservingInteractionLod: boolean
  renderActiveEdgesOnly: boolean
  renderOnlyVisibleElements: boolean
  snapToGrid: boolean
  viewportPreviewLayer: boolean
}

export const CANVAS_PERFORMANCE_SETTINGS_STORAGE_KEY = 'dreamwright_canvas_performance_settings_v2'

export const DEFAULT_CANVAS_PERFORMANCE_SETTINGS: CanvasPerformanceSettings = {
  alwaysHideEdges: false,
  hideEdgesDuringInteraction: true,
  imagePreviewOnlyDuringInteraction: false,
  lightweightImageNodeShell: false,
  lightweightNodeChrome: false,
  previewPreservingInteractionLod: false,
  // Default-on fast path: only render edges (and, paired with per-node handle
  // gating, handles) for "active" nodes — selected / hovered / running — plus the
  // endpoints needed to anchor visible edges. Keeps large canvases responsive
  // without permanently hiding edges the way `alwaysHideEdges` does.
  renderActiveEdgesOnly: true,
  renderOnlyVisibleElements: true,
  // Snap dragged nodes to the canvas grid dots (CANVAS_GRID_SIZE px pitch).
  // User-facing toggle; defaults ON so layouts stay aligned without anyone
  // having to find the setting, and a stored `false` still disables snapping.
  //
  // Raising this default only reaches browsers with NOTHING stored, which in
  // practice is almost nobody: `useLocalStorage` persists from an unconditional
  // mount effect, so any browser that has opened a workspace already holds an
  // explicit value. `defaultOnMigrations.ts` does the one-time flip for those.
  snapToGrid: true,
  viewportPreviewLayer: false,
}

function boolOrDefault(value: boolean | undefined, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

export function normalizeCanvasPerformanceSettings(
  value: Partial<CanvasPerformanceSettings> | null | undefined,
): CanvasPerformanceSettings {
  // Only the user-facing controls (alwaysHideEdges, lightweightNodeChrome,
  // renderOnlyVisibleElements, snapToGrid) should vary from stored values. The remaining
  // keys are pinned to their defaults so stale localStorage from earlier builds
  // cannot affect rendering without a visible way to turn the behavior off.
  // `renderActiveEdgesOnly` is now the default-on fast path (not a retired
  // experiment); it stays pinned because there is no UI toggle and a stale
  // `false` from old localStorage must not disable the fast path.
  return {
    alwaysHideEdges: boolOrDefault(
      value?.alwaysHideEdges,
      DEFAULT_CANVAS_PERFORMANCE_SETTINGS.alwaysHideEdges,
    ),
    hideEdgesDuringInteraction: DEFAULT_CANVAS_PERFORMANCE_SETTINGS.hideEdgesDuringInteraction,
    imagePreviewOnlyDuringInteraction: DEFAULT_CANVAS_PERFORMANCE_SETTINGS.imagePreviewOnlyDuringInteraction,
    lightweightImageNodeShell: DEFAULT_CANVAS_PERFORMANCE_SETTINGS.lightweightImageNodeShell,
    lightweightNodeChrome: boolOrDefault(
      value?.lightweightNodeChrome,
      DEFAULT_CANVAS_PERFORMANCE_SETTINGS.lightweightNodeChrome,
    ),
    previewPreservingInteractionLod: DEFAULT_CANVAS_PERFORMANCE_SETTINGS.previewPreservingInteractionLod,
    renderActiveEdgesOnly: DEFAULT_CANVAS_PERFORMANCE_SETTINGS.renderActiveEdgesOnly,
    renderOnlyVisibleElements: boolOrDefault(
      value?.renderOnlyVisibleElements,
      DEFAULT_CANVAS_PERFORMANCE_SETTINGS.renderOnlyVisibleElements,
    ),
    snapToGrid: boolOrDefault(
      value?.snapToGrid,
      DEFAULT_CANVAS_PERFORMANCE_SETTINGS.snapToGrid,
    ),
    viewportPreviewLayer: DEFAULT_CANVAS_PERFORMANCE_SETTINGS.viewportPreviewLayer,
  }
}
